import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useSession } from '../lib/session';
import type { CompanySummary } from '../lib/types';
import { COMPANY_STATUS_LABELS, ROLE_LABELS } from '../lib/types';
import { KindBadge } from '../components/Badges';
import { Modal } from '../components/Modal';

export function CompaniesPage() {
  const { session, refresh } = useSession();
  const [companies, setCompanies] = useState<CompanySummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const isAdmin = !!session?.person.isAdmin;

  async function load() {
    try {
      setCompanies(await api.get<CompanySummary[]>('/companies'));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load companies');
    }
  }

  useEffect(() => {
    void load();
  }, []);

  const internal = companies?.filter((c) => c.kind === 'internal') ?? [];
  const external = companies?.filter((c) => c.kind === 'external') ?? [];

  return (
    <>
      <div className="page-head">
        <div>
          <div className="eyebrow">Administration</div>
          <h1>Companies</h1>
          <p className="subtitle">
            Every company the group delivers for — Egen's own businesses and external
            customers. A company holds its projects, its people and its priorities.
          </p>
        </div>
        {isAdmin && (
          <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
            Add company
          </button>
        )}
      </div>

      {error && <div className="callout mb-2">{error}</div>}

      {companies === null ? (
        <div className="empty">Loading companies…</div>
      ) : companies.length === 0 ? (
        <div className="card">
          <div className="empty">
            <h3>No companies yet</h3>
            <p>{isAdmin ? 'Add the first company to start setting up projects.' : 'You have not been given access to a company yet.'}</p>
            {isAdmin && (
              <button type="button" className="btn btn-primary mt-2" onClick={() => setCreating(true)}>
                Add company
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="stack">
          <Section title="Egen group" companies={internal} />
          <Section title="Customers" companies={external} />
        </div>
      )}

      {creating && (
        <CreateCompanyModal
          onClose={() => setCreating(false)}
          onCreated={async () => {
            setCreating(false);
            /**
             * The SESSION has to be refetched, not just this page's list.
             *
             * Creating a company also creates your membership of it — an administrator holds
             * one of every company — and the session carries that list. Everything downstream
             * reads it: the timesheet's project picker, the company selectors, the sidebar.
             * Reloading only this page left the app believing you were not a member of the
             * company you had just made, so its projects could not be booked against until
             * somebody reloaded the browser.
             */
            await refresh();
            void load();
          }}
        />
      )}
    </>
  );
}

function Section({ title, companies }: { title: string; companies: CompanySummary[] }) {
  if (companies.length === 0) return null;
  return (
    <div>
      <div className="nav-group-label" style={{ padding: '0 0 10px' }}>
        {title} · {companies.length}
      </div>
      <div className="grid grid-cards">
        {companies.map((company) => (
          <Link key={company.id} to={`/companies/${company.id}`} className="card card-pad" style={{ display: 'block' }}>
            <div className="row-between" style={{ marginBottom: 10 }}>
              <span className="key">{company.code}</span>
              <KindBadge kind={company.kind} />
            </div>
            <h3 style={{ fontSize: 17, marginBottom: 4 }}>{company.name}</h3>
            <div className="dim" style={{ fontSize: 12 }}>
              {COMPANY_STATUS_LABELS[company.status]}
              {company.role ? ` · you are ${ROLE_LABELS[company.role].toLowerCase()}` : ''}
            </div>
            <div className="row" style={{ gap: 16, marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--line)' }}>
              <Metric value={company.activeProjectCount} label="active" />
              <Metric value={company.projectCount} label="projects" />
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function Metric({ value, label }: { value: number; label: string }) {
  return (
    <div>
      <div className="ui-font" style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.03em' }}>
        {value}
      </div>
      <div className="stat-label">{label}</div>
    </div>
  );
}

function CreateCompanyModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [kind, setKind] = useState<'internal' | 'external'>('external');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Suggest a code from the name, but let it be overridden — it is permanent once set.
  function onNameChange(value: string) {
    const previousSuggestion = suggestCode(name);
    setName(value);
    if (!code || code === previousSuggestion) setCode(suggestCode(value));
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.post('/companies', {
        name,
        code: code.toUpperCase(),
        kind,
        primaryContactName: contactName || undefined,
        primaryContactEmail: contactEmail || undefined,
        notes: notes || undefined,
      });
      onCreated();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create the company');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Add company"
      subtitle="A company is the outer boundary: its people, projects and data stay inside it."
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-quiet" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="create-company" className="btn btn-primary" disabled={busy}>
            {busy ? 'Adding…' : 'Add company'}
          </button>
        </>
      }
    >
      {error && <div className="callout">{error}</div>}
      <form id="create-company" onSubmit={submit} noValidate>
        <label className="field">
          <span className="field-label">Company name</span>
          <input type="text" value={name} onChange={(e) => onNameChange(e.target.value)} required autoFocus />
        </label>

        <label className="field">
          <span className="field-label">Short code</span>
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            maxLength={12}
            required
            className="mono"
          />
          <span className="field-hint">
            Prefixes every work item, e.g. {code || 'CODE'}-VAULT-17. Permanent once set.
          </span>
        </label>

        <label className="field">
          <span className="field-label">Type</span>
          <select value={kind} onChange={(e) => setKind(e.target.value as 'internal' | 'external')}>
            <option value="internal">Egen group company</option>
            <option value="external">External customer</option>
          </select>
        </label>

        <label className="field">
          <span className="field-label">Primary contact (optional)</span>
          <input type="text" value={contactName} onChange={(e) => setContactName(e.target.value)} placeholder="Name" />
        </label>

        <label className="field">
          <span className="field-label">Contact email (optional)</span>
          <input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} />
        </label>

        <label className="field">
          <span className="field-label">Notes (optional)</span>
          <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </label>
      </form>
    </Modal>
  );
}

function suggestCode(name: string): string {
  const words = name.trim().toUpperCase().split(/[^A-Z0-9]+/).filter(Boolean);
  if (words.length === 0) return '';
  if (words.length === 1) return words[0].slice(0, 6);
  return words.map((w) => w[0]).join('').slice(0, 6);
}
